<template>
  <div class="property-list-v2">

    <!-- Filter panel -->
    <filter-panel-v2
      ref="filterPanel"
      :initial-filter="filter"
      :toolbar-status="toolbarStatusObj"
      :toolbar-expiring-soon="toolbarExpiringSoon"
      @apply-filter="applyFilter"
      @clear-filter="onClearFilter"
      @clear-toolbar-status="onToolbarStatusChange(null)"
      @clear-toolbar-expiring="onToolbarExpiringSoon(false); toolbarExpiringSoon = false"
    />

    <!-- Comment sidebar -->
    <b-sidebar v-model="isSidebarCommentOpen" right shadow backdrop no-header :width="sidebarWidth + 'px'" body-class="p-0 d-flex flex-column">
      <!-- Resize handle -->
      <div class="sidebar-resize-handle" @mousedown="startSidebarResize" />
      <div class="d-flex align-items-center justify-content-between px-1 py-75 border-bottom" style="flex-shrink: 0;">
        <h5 class="mb-0 font-weight-bold">Comments</h5>
        <b-button variant="flat-secondary" size="sm" class="p-25" @click="isSidebarCommentOpen = false">
          <feather-icon icon="XIcon" size="16" />
        </b-button>
      </div>
      <comments v-if="isSidebarCommentOpen" :id="commentPropertyId" @childToClose="isSidebarCommentOpen = false" />
    </b-sidebar>

    <!-- Mail sidebar -->
    <mail-form
      :is-mail-form-sidebar-active.sync="isMailFormSidebarActive"
      :property-data="mailPropertyData"
      @refetch-data="fetchProperties"
    />

    <!-- Toolbar -->
    <b-card no-body>
      <div class="d-flex justify-content-between align-items-center flex-wrap px-1 pt-1 pb-50" style="gap: 0.5rem;">
        <!-- Status + Expiring Soon (start) -->
        <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
          <b-dropdown :text="toolbarStatusObj ? toolbarStatusObj.name : 'All Status'" variant="outline-secondary" size="sm" no-flip>
            <b-dropdown-item :active="!toolbarStatusObj" @click="onToolbarStatusChange(null)">All Status</b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item v-for="s in statuses" :key="s.id" :active="toolbarStatusObj && toolbarStatusObj.id === s.id" @click="onToolbarStatusChange(s)">
              {{ s.name }}
            </b-dropdown-item>
          </b-dropdown>
          <b-form-checkbox v-model="toolbarExpiringSoon" class="text-nowrap d-none d-md-flex" @change="onToolbarExpiringSoon">
            Expiring Soon
          </b-form-checkbox>
          <!-- Sold is left out of the list unless this is ticked -->
          <b-form-checkbox :checked="filter.showSold" class="text-nowrap" @change="v => applyFilter({ showSold: !!v })">
            Sold
          </b-form-checkbox>
        </div>
        <!-- New property + Save Search buttons — visible sm–md only; lg+ shows them in filter panel -->
        <div class="d-none d-sm-flex d-lg-none align-items-center" style="gap: 0.5rem;">
          <b-button variant="primary" size="sm" :to="{ name: 'property-create' }">
            <feather-icon icon="PlusIcon" size="14" class="mr-25" />New Property
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="$refs.filterPanel.saveTemplate()">
            <feather-icon icon="SaveIcon" size="14" class="mr-25" />Save Search
          </b-button>
        </div>
        <div class="d-flex align-items-center ml-auto flex-wrap" style="gap: 0.5rem;">
          <!-- Per page -->
          <b-dropdown :text="'Show ' + perPage" variant="outline-secondary" size="sm" no-flip class="d-none d-md-block">
            <b-dropdown-item v-for="n in perPageOptions" :key="n" :active="perPage === n" @click="perPage = n; currentPage = 1; fetchProperties()">
              {{ n }}
            </b-dropdown-item>
          </b-dropdown>
          <!-- Sort -->
          <b-dropdown :text="sortLabel" variant="outline-secondary" size="sm" right no-flip>
            <b-dropdown-item v-for="opt in sortOptions" :key="opt.value" :active="opt.value === sortOption" @click="sortOption = opt.value; fetchProperties()">
              {{ opt.text }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- ══ DESKTOP TABLE ══ -->
      <b-overlay :show="isLoading" rounded="sm" class="d-none d-md-block">
        <b-table
          :items="properties"
          :fields="tableFields"
          responsive
          striped
          hover
          show-empty
          empty-text="No properties found"
          class="mb-0"
        >
          <!-- Image -->
          <template #cell(image)="{ item }">
            <div class="prop-thumb">
              <router-link :to="{ name: 'property-upload', params: { id: item.id } }">
                <b-img
                  :src="item.property_images && item.property_images[0] ? item.property_images[0].fullfilepath : defaultImg"
                  width="160"
                  height="120"
                  style="object-fit: cover; border-radius: 4px; cursor: pointer;"
                />
              </router-link>
            </div>
          </template>

          <!-- Property info -->
          <template #cell(property)="{ item }">
            <div>
              <div class="d-flex align-items-center" style="gap: 0.35rem;">
                <router-link :to="{ name: 'property-edit', params: { id: item.id } }" class="font-weight-bold" :class="{ 'text-danger': !item.is_active }">
                  {{ item.property_code }}
                </router-link>
                <b-dropdown variant="link" no-caret toggle-class="text-decoration-none p-0" right>
                  <template #button-content>
                    <feather-icon icon="Share2Icon" size="13" class="text-primary" />
                  </template>
                  <b-dropdown-item :href="frontendUrl(item)" target="_blank">
                    <feather-icon icon="ExternalLinkIcon" size="14" class="mr-50" />Link
                  </b-dropdown-item>
                  <b-dropdown-item @click="copyLink(item)">
                    <feather-icon icon="CopyIcon" size="14" class="mr-50" />Copy Link
                  </b-dropdown-item>
                  <b-dropdown-item @click="shareWithText(item)">
                    <feather-icon icon="MessageSquareIcon" size="14" class="mr-50" />Share with Text
                  </b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item @click="copyLinkNetwork(item)">
                    <feather-icon icon="GlobeIcon" size="14" class="mr-50" />Share To Network
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <span class="d-block text-dark">{{ item.place_name && item.place_name[$i18n.locale] }}</span>
              <div class="d-flex flex-wrap mt-25" style="gap: 0.25rem;">
                <b-badge :variant="actionVariant(item.action_id)" class="text-capitalize">{{ item.action_name }}</b-badge>
                <b-badge :variant="item.status_id === 1 ? 'light-success' : 'light-secondary'">{{ item.status_code }}</b-badge>
              </div>
              <small v-if="item.availble_date" class="text-muted d-block mt-25">Avl: {{ item.availble_date }}</small>
              <small v-if="item.expire_date"   class="text-muted d-block">Exp: {{ item.expire_date }}</small>
              <div class="d-flex align-items-center mt-25" style="gap: 0.35rem;">
                <b-button variant="flat-secondary" size="sm" class="p-0" @click="openComment(item.id)">
                  <feather-icon icon="MessageSquareIcon" size="15" class="text-secondary" />
                </b-button>
                <b-button variant="flat-info" size="sm" class="p-0" @click="addToCart(item)">
                  <feather-icon icon="ShoppingCartIcon" size="15" class="text-info" />
                </b-button>
                <b-button variant="flat-warning" size="sm" class="p-0" @click="mailPropertyData = item; isMailFormSidebarActive = true">
                  <feather-icon icon="MailIcon" size="15" class="text-warning" />
                </b-button>
                <b-button variant="flat-primary" size="sm" class="p-0" :disabled="postingIds.has(item.id)" @click="socialPost(item)">
                  <b-spinner v-if="postingIds.has(item.id)" small style="width:15px;height:15px;" />
                  <feather-icon v-else icon="FacebookIcon" size="15" class="text-primary" />
                </b-button>
              </div>
              <p v-if="item.comments && item.comments[0]" class="mb-0 mt-25 text-warning text-truncate" style="max-width: 220px; font-size: 0.8rem;">
                {{ stripTags(item.comments[0].comment) }}
              </p>
            </div>
          </template>

          <!-- Owner -->
          <template #cell(owner)="{ item }">
            <div v-if="item.owner_id" class="d-flex align-items-center" style="gap: 0.35rem;">
              <span class="text-primary font-weight-bold cursor-pointer" @click="toggleRowDetails(item)">
                {{ item.owner_firstname }} {{ item.owner_lastname }}
              </span>
              <router-link :to="{ name: 'clients-edit', params: { id: item.owner_id } }" target="_blank">
                <feather-icon icon="Edit2Icon" size="11" :class="item._showDetails ? 'text-primary' : 'text-muted'" />
              </router-link>
            </div>
            <span v-else class="text-muted">–</span>
          </template>

          <!-- Unit -->
          <template #cell(unit)="{ item }">
            <span v-if="item.address_no || item.units">
              <span v-if="item.address_no">{{ item.address_no }}</span>
              <span v-if="item.address_no && item.units"> / </span>
              <span v-if="item.units">{{ item.units }}</span>
            </span>
            <span v-else class="text-muted">–</span>
          </template>

          <!-- Floor -->
          <template #cell(floor)="{ item }">{{ item.floors || '–' }}</template>

          <!-- Building -->
          <template #cell(building)="{ item }">{{ item.buildiing || '–' }}</template>

          <!-- Bedrooms -->
          <template #cell(bedroom)="{ item }">
            <span v-if="item.bedroom === 0">Studio</span>
            <span v-else>{{ item.bedroom || '–' }}</span>
          </template>

          <!-- SQM -->
          <template #cell(sqm)="{ item }">{{ item.sqm ? item.sqm.toLocaleString() : '–' }}</template>

          <!-- Unit Type -->
          <template #cell(type_name)="{ item }">{{ item.type_name || '–' }}</template>

          <!-- Rent price -->
          <template #cell(rent_price)="{ item }">
            <span v-if="hasOffer(item.rent_price)" class="text-nowrap">{{ priceCell(item.rent_price) }}</span>
            <span v-else class="text-muted">–</span>
          </template>

          <!-- Sale price -->
          <template #cell(sale_price)="{ item }">
            <span v-if="hasOffer(item.sale_price)" class="text-nowrap">{{ priceCell(item.sale_price) }}</span>
            <span v-else class="text-muted">–</span>
          </template>

          <!-- Actions -->
          <template #cell(actions)="{ item }">
            <b-dropdown variant="link" no-caret toggle-class="text-decoration-none p-0" right>
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="text-body" />
              </template>
              <b-dropdown-item :to="{ name: 'property-edit', params: { id: item.id } }">
                <feather-icon icon="Edit2Icon" size="14" class="mr-50" />Edit
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'property-upload', params: { id: item.id } }">
                <feather-icon icon="ImageIcon" size="14" class="mr-50" />Upload Images
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item variant="danger" @click="confirmDelete(item.id)">
                <feather-icon icon="TrashIcon" size="14" class="mr-50" />Delete
              </b-dropdown-item>
            </b-dropdown>
          </template>

          <!-- Row details (owner click) -->
          <template #row-details="{ item }">
            <div v-if="editForms[item.id]" class="row-detail-panel px-2 pt-1 pb-75" style="position: relative;">

              <!-- Close button -->
              <b-button variant="flat-secondary" size="sm" class="detail-close-btn" @click="$set(item, '_showDetails', false)">
                <feather-icon icon="XIcon" size="16" />
              </b-button>

              <!-- Owner info -->
              <div class="detail-section-label">Owner</div>
              <b-row class="mb-75">
                <b-col cols="12" sm="4" md="3">
                  <span class="detail-key">Full Name</span>
                  <router-link :to="{ name: 'clients-edit', params: { id: item.owner_id } }" target="_blank" class="d-block font-weight-bold">
                    {{ item.owner_firstname }} {{ item.owner_lastname }}
                  </router-link>
                </b-col>
                <b-col cols="12" sm="4" md="3">
                  <span class="detail-key">Email</span>
                  <template v-if="item.emails && item.emails.length">
                    <a v-for="e in item.emails" :key="e.id" :href="'mailto:' + e.email" class="d-block text-truncate">{{ e.email }}</a>
                  </template>
                  <span v-else class="text-muted d-block">–</span>
                </b-col>
                <b-col cols="12" sm="4" md="3">
                  <span class="detail-key">Phone</span>
                  <template v-if="item.phones && item.phones.length">
                    <a v-for="p in item.phones" :key="p.id" :href="'tel:' + p.phone_no" class="d-block">{{ p.phone_no }}</a>
                  </template>
                  <span v-else class="text-muted d-block">–</span>
                </b-col>
              </b-row>

              <!-- Editable fields -->
              <div class="detail-section-label">Update Info</div>
              <div class="d-flex flex-wrap mb-75" style="gap: 0.75rem;">
                <div class="edit-field-col">
                  <label class="detail-label">Available Date</label>
                  <b-form-datepicker v-model="editForms[item.id].available_date" reset-button />
                </div>
                <div class="edit-field-col">
                  <label class="detail-label">Expire Date</label>
                  <b-form-datepicker v-model="editForms[item.id].expire_date" reset-button />
                </div>
                <div class="edit-field-col">
                  <label class="detail-label">Sale Price (฿)</label>
                  <input
                    class="form-control"
                    type="text"
                    :value="formatPriceInput(editForms[item.id].sale_price)"
                    placeholder="–"
                    @input="e => onPriceInput(item.id, 'sale_price', e)"
                  />
                </div>
                <div class="edit-field-col">
                  <label class="detail-label">Rent Price (฿)</label>
                  <input
                    class="form-control"
                    type="text"
                    :value="formatPriceInput(editForms[item.id].rent_price)"
                    placeholder="–"
                    @input="e => onPriceInput(item.id, 'rent_price', e)"
                  />
                </div>
                <div class="edit-field-col">
                  <label class="detail-label">Status</label>
                  <v-select v-model="editForms[item.id].statusObj" :options="statuses" label="name" :clearable="true" />
                </div>
              </div>

              <!-- Actions -->
              <div class="d-flex align-items-center" style="gap: 0.5rem;">
                <b-button variant="primary" size="sm" :disabled="editForms[item.id].isUpdating" @click="updatePropertyQuick(item)">
                  <b-spinner v-if="editForms[item.id].isUpdating" small class="mr-25" />Update
                </b-button>
              </div>
            </div>
          </template>
        </b-table>
      </b-overlay>

      <!-- ══ MOBILE CARDS ══ -->
      <div class="d-md-none">
        <b-overlay :show="isLoading" rounded="sm">
          <div v-if="!properties.length && !isLoading" class="text-center py-3 text-muted">
            No properties found
          </div>
          <div v-for="item in properties" :key="item.id" class="prop-card border-bottom px-1 py-75">
            <div class="d-flex" style="gap: 0.75rem;">
              <!-- Thumbnail -->
              <div class="flex-shrink-0" style="width: 160px;">
                <b-img
                  :src="item.property_images && item.property_images[0] ? item.property_images[0].fullfilepath : defaultImg"
                  width="160"
                  height="120"
                  style="object-fit: cover; border-radius: 6px;"
                />
              </div>
              <!-- Info -->
              <div class="flex-grow-1 min-width-0">
                <router-link :to="{ name: 'property-edit', params: { id: item.id } }" class="font-weight-bold d-block text-truncate" :class="{ 'text-danger': !item.is_active }">
                  {{ item.property_code }}
                </router-link>
                <small class="text-muted d-block text-truncate">{{ item.place_name && item.place_name[$i18n.locale] }}</small>
                <div class="d-flex flex-wrap mt-25" style="gap: 0.25rem;">
                  <b-badge :variant="actionVariant(item.action_id)">{{ item.action_name }}</b-badge>
                  <b-badge :variant="item.status_id === 1 ? 'light-success' : 'light-secondary'">{{ item.status_code }}</b-badge>
                </div>
                <small v-if="item.address_no || item.units" class="text-muted d-block mt-25">
                  <feather-icon icon="HomeIcon" size="11" /> {{ [item.address_no, item.units].filter(Boolean).join(' / ') }}
                </small>
                <div class="d-flex flex-wrap mt-25" style="gap: 0.4rem;">
                  <small v-if="item.bedroom !== null" class="text-muted">
                    <feather-icon icon="BedIcon" size="11" /> {{ item.bedroom === 0 ? 'Studio' : item.bedroom + ' Br' }}
                  </small>
                  <small v-if="item.sqm" class="text-muted">
                    <feather-icon icon="MaximizeIcon" size="11" /> {{ item.sqm }} sqm
                  </small>
                  <small v-if="item.floors" class="text-muted">
                    <feather-icon icon="LayersIcon" size="11" /> Fl. {{ item.floors }}
                  </small>
                </div>
                <div class="mt-25">
                  <span v-if="hasOffer(item.rent_price)" class="text-primary font-weight-bold d-block" style="font-size: 0.9rem;">{{ priceCell(item.rent_price) }}{{ isOnRequest(item.rent_price) ? '' : '/mo' }}</span>
                  <span v-if="hasOffer(item.sale_price)" class="text-success font-weight-bold d-block" style="font-size: 0.9rem;">{{ priceCell(item.sale_price) }}</span>
                </div>
              </div>
              <!-- Card menu -->
              <div class="flex-shrink-0">
                <b-dropdown variant="link" no-caret toggle-class="p-0" right>
                  <template #button-content>
                    <feather-icon icon="MoreVerticalIcon" size="16" class="text-muted" />
                  </template>
                  <b-dropdown-item :to="{ name: 'property-edit', params: { id: item.id } }">Edit</b-dropdown-item>
                  <b-dropdown-item :to="{ name: 'property-upload', params: { id: item.id } }">Images</b-dropdown-item>
                  <b-dropdown-item variant="danger" @click="confirmDelete(item.id)">Delete</b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <small v-if="item.owner_firstname" class="d-block mt-25">
              <feather-icon icon="UserIcon" size="11" class="text-muted" />
              <span class="text-primary font-weight-bold cursor-pointer" @click="openMobileEdit(item)">
                {{ item.owner_firstname }} {{ item.owner_lastname }}
              </span>
            </small>
            <!-- Action icons row -->
            <div class="d-flex align-items-center mt-50" style="gap: 1.25rem;">
              <b-button variant="flat-secondary" size="sm" class="mobile-action-btn" @click="openComment(item.id)">
                <feather-icon icon="MessageSquareIcon" size="18" class="text-secondary" />
              </b-button>
              <b-button variant="flat-info" size="sm" class="mobile-action-btn" @click="addToCart(item)">
                <feather-icon icon="ShoppingCartIcon" size="18" class="text-info" />
              </b-button>
              <b-button variant="flat-warning" size="sm" class="mobile-action-btn" @click="mailPropertyData = item; isMailFormSidebarActive = true">
                <feather-icon icon="MailIcon" size="18" class="text-warning" />
              </b-button>
              <b-button variant="flat-primary" size="sm" class="mobile-action-btn" :disabled="postingIds.has(item.id)" @click="socialPost(item)">
                <b-spinner v-if="postingIds.has(item.id)" small style="width:18px;height:18px;" />
                <feather-icon v-else icon="FacebookIcon" size="18" class="text-primary" />
              </b-button>
              <b-button variant="flat-primary" size="sm" class="mobile-action-btn" @click="shareSheetItem = item">
                <feather-icon icon="Share2Icon" size="18" class="text-primary" />
              </b-button>
            </div>
          </div>
        </b-overlay>

        <!-- Edit bottom sheet -->
        <transition name="slide-up">
          <div v-if="mobileEditItem && editForms[mobileEditItem.id]" class="share-sheet-backdrop" @click.self="mobileEditItem = null">
            <div class="share-sheet" style="max-height: 85vh; overflow-y: auto; align-items: stretch;">
              <div class="share-sheet-handle" style="align-self: center;" />

              <!-- Header -->
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                  <div class="font-weight-bold text-primary">{{ mobileEditItem.owner_firstname }} {{ mobileEditItem.owner_lastname }}</div>
                  <small class="text-muted">{{ mobileEditItem.property_code }}</small>
                </div>
                <b-button variant="flat-secondary" size="sm" class="p-25" @click="mobileEditItem = null">
                  <feather-icon icon="XIcon" size="16" />
                </b-button>
              </div>

              <!-- Owner contacts -->
              <div class="mb-1" style="font-size: 0.82rem;">
                <template v-if="mobileEditItem.emails && mobileEditItem.emails.length">
                  <a v-for="e in mobileEditItem.emails" :key="e.id" :href="'mailto:' + e.email" class="d-block text-truncate">
                    <feather-icon icon="MailIcon" size="11" class="mr-25" />{{ e.email }}
                  </a>
                </template>
                <template v-if="mobileEditItem.phones && mobileEditItem.phones.length">
                  <a v-for="p in mobileEditItem.phones" :key="p.id" :href="'tel:' + p.phone_no" class="d-block">
                    <feather-icon icon="PhoneIcon" size="11" class="mr-25" />{{ p.phone_no }}
                  </a>
                </template>
              </div>

              <!-- Edit fields -->
              <div class="mb-75">
                <label class="detail-label">Status</label>
                <v-select v-model="editForms[mobileEditItem.id].statusObj" :options="statuses" label="name" :clearable="true" />
              </div>
              <div class="mb-75">
                <label class="detail-label">Available Date</label>
                <b-form-datepicker v-model="editForms[mobileEditItem.id].available_date" reset-button />
              </div>
              <div class="mb-75">
                <label class="detail-label">Expire Date</label>
                <b-form-datepicker v-model="editForms[mobileEditItem.id].expire_date" reset-button />
              </div>
              <div class="mb-75">
                <label class="detail-label">Sale Price (฿)</label>
                <input
                  class="form-control"
                  type="text"
                  :value="formatPriceInput(editForms[mobileEditItem.id].sale_price)"
                  placeholder="–"
                  @input="e => onPriceInput(mobileEditItem.id, 'sale_price', e)"
                />
              </div>
              <div class="mb-1">
                <label class="detail-label">Rent Price (฿)</label>
                <input
                  class="form-control"
                  type="text"
                  :value="formatPriceInput(editForms[mobileEditItem.id].rent_price)"
                  placeholder="–"
                  @input="e => onPriceInput(mobileEditItem.id, 'rent_price', e)"
                />
              </div>

              <b-button variant="primary" block :disabled="editForms[mobileEditItem.id].isUpdating" @click="updatePropertyQuick(mobileEditItem)">
                <b-spinner v-if="editForms[mobileEditItem.id].isUpdating" small class="mr-25" />Update
              </b-button>
            </div>
          </div>
        </transition>

        <!-- Share bottom sheet -->
        <transition name="slide-up">
          <div v-if="shareSheetItem" class="share-sheet-backdrop" @click.self="shareSheetItem = null">
            <div class="share-sheet">
              <div class="share-sheet-handle" />
              <p class="share-sheet-title text-truncate">{{ shareSheetItem.place_name && shareSheetItem.place_name[$i18n.locale] }}</p>
              <div class="share-sheet-actions">
                <a class="share-sheet-btn" :href="frontendUrl(shareSheetItem)" target="_blank" @click="shareSheetItem = null">
                  <div class="share-sheet-icon"><feather-icon icon="ExternalLinkIcon" size="20" /></div>
                  <span>Link</span>
                </a>
                <button class="share-sheet-btn" @click="copyLink(shareSheetItem); shareSheetItem = null">
                  <div class="share-sheet-icon"><feather-icon icon="CopyIcon" size="20" /></div>
                  <span>Copy Link</span>
                </button>
                <button class="share-sheet-btn" @click="shareWithText(shareSheetItem); shareSheetItem = null">
                  <div class="share-sheet-icon"><feather-icon icon="MessageSquareIcon" size="20" /></div>
                  <span>Share Text</span>
                </button>
                <button class="share-sheet-btn" @click="copyLinkNetwork(shareSheetItem); shareSheetItem = null">
                  <div class="share-sheet-icon"><feather-icon icon="GlobeIcon" size="20" /></div>
                  <span>Network</span>
                </button>
              </div>
              <button class="share-sheet-cancel" @click="shareSheetItem = null">Cancel</button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center flex-wrap px-1 py-75 border-top" style="gap: 0.5rem;">
        <small class="text-muted w-100 text-center d-lg-none">Showing {{ dataMeta.from }}–{{ dataMeta.to }} of {{ dataMeta.of }} properties</small>
        <small class="text-muted d-none d-lg-block">Showing {{ dataMeta.from }}–{{ dataMeta.to }} of {{ dataMeta.of }} properties</small>
        <b-pagination
          v-model="currentPage"
          :total-rows="total"
          :per-page="perPage"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          class="mb-0 mx-auto mx-lg-0"
          @change="onPageChange"
        >
          <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
          <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
        </b-pagination>
      </div>
    </b-card>

  </div>
</template>

<script>
import {
  BCard, BOverlay, BTable, BImg, BBadge, BButton, BPagination,
  BDropdown, BDropdownItem, BDropdownDivider, BFormCheckbox,
  BRow, BCol, BFormInput, BFormDatepicker, BSpinner, BSidebar,
} from 'bootstrap-vue'
import Comments from '@/views/components/comments/Comments.vue'
import MailForm from '@/views/properties/property-list/MailForm.vue'
import vSelect from 'vue-select'
import axios from '@axios'
import moment from 'moment'
import usePropertyListV2 from './usePropertyListV2'

export default {
  name: 'PropertyListV2',
  components: {
    BCard, BOverlay, BTable, BImg, BBadge, BButton, BPagination,
    BDropdown, BDropdownItem, BDropdownDivider, BFormCheckbox,
    BRow, BCol, BFormInput, BFormDatepicker, BSpinner, BSidebar,
    vSelect,
    Comments,
    MailForm,
    FilterPanelV2: () => import('./FilterPanelV2.vue'),
  },
  setup() {
    const {
      isLoading, properties, total, perPage, currentPage, perPageOptions,
      sortOption, filter, dataMeta, fetchProperties, applyFilter, clearFilter, deleteProperty,
    } = usePropertyListV2()

    return {
      isLoading, properties, total, perPage, currentPage, perPageOptions,
      sortOption, filter, dataMeta, fetchProperties, applyFilter, clearFilter, deleteProperty,
    }
  },
  mounted() {
    axios.post('/property/v1/status').then(r => {
      this.statuses = r.data.data || []
      if (this.filter.statusId) {
        this.toolbarStatusObj = this.statuses.find(s => s.id === this.filter.statusId) || null
      }
    }).catch(() => {})
  },
  data() {
    return {
      postingIds: new Set(),
      statuses: [],
      toolbarStatusObj: null,
      toolbarExpiringSoon: false,
      editForms: {},
      isSidebarCommentOpen: false,
      commentPropertyId: null,
      sidebarWidth: 400,
      isMailFormSidebarActive: false,
      mailPropertyData: null,
      shareSheetItem: null,
      mobileEditItem: null,
      defaultImg: 'https://findbangkokroom.com/public/img/storage/1698858264911-picture.png',
      tableFields: [
        { key: 'image',      label: 'Image',    tdClass: 'align-middle', thStyle: 'width: 180px' },
        { key: 'property',   label: 'Property', tdClass: 'align-middle' },
        { key: 'owner',      label: 'Owner',    tdClass: 'align-middle' },
        { key: 'unit',       label: 'House / Unit', tdClass: 'align-middle text-center', thClass: 'text-center' },
        { key: 'floor',      label: 'Floor',    tdClass: 'align-middle text-center', thClass: 'text-center' },
        { key: 'building',   label: 'Building', tdClass: 'align-middle' },
        { key: 'bedroom',    label: 'Br',       tdClass: 'align-middle text-center', thClass: 'text-center' },
        { key: 'sqm',        label: 'SQM',      tdClass: 'align-middle text-right',  thClass: 'text-right' },
        { key: 'type_name',  label: 'Type',     tdClass: 'align-middle' },
        { key: 'rent_price', label: 'Rent',     tdClass: 'align-middle text-right',  thClass: 'text-right' },
        { key: 'sale_price', label: 'Sale',     tdClass: 'align-middle text-right',  thClass: 'text-right' },
        { key: 'actions',    label: '',         tdClass: 'align-middle' },
      ],
      sortOptions: [
        { value: 'newest',            text: 'Newest First' },
        { value: 'sale_price_asc',    text: 'Price Sale ↑' },
        { value: 'sale_price_desc',   text: 'Price Sale ↓' },
        { value: 'rent_price_asc',    text: 'Price Rent ↑' },
        { value: 'rent_price_desc',   text: 'Price Rent ↓' },
        { value: 'bedroom_asc',       text: 'Bedroom ↑' },
        { value: 'bedroom_desc',      text: 'Bedroom ↓' },
        { value: 'floor_asc',         text: 'Floor ↑' },
        { value: 'floor_desc',        text: 'Floor ↓' },
        { value: 'sqm_asc',           text: 'SQM ↑' },
        { value: 'sqm_desc',          text: 'SQM ↓' },
      ],
    }
  },
  computed: {
    sortLabel() {
      const opt = this.sortOptions.find(o => o.value === this.sortOption)
      return opt ? opt.text : 'Sort'
    },
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchProperties()
    },
    actionVariant(id) {
      const map = { 1: 'light-secondary', 2: 'light-info', 3: 'light-success', 4: 'light-warning' }
      return map[id] || 'light-secondary'
    },
    stripTags(str) {
      if (!str) return ''
      return str.replace(/<[^>]*>/g, '')
    },
    confirmDelete(id) {
      this.$bvModal.msgBoxConfirm('Delete this property?', {
        title: 'Confirm Delete', size: 'sm', okVariant: 'danger',
        okTitle: 'Delete', cancelTitle: 'Cancel', centered: true,
      }).then(ok => { if (ok) this.deleteProperty(id) })
    },
    onToolbarStatusChange(v) {
      this.toolbarStatusObj = v
      this.applyFilter({ statusId: v ? v.id : null })
    },
    onToolbarExpiringSoon(v) {
      if (v) {
        this.applyFilter({
          minDateExpire: moment().format('YYYY-MM-DD'),
          maxDateExpire: moment().add(2, 'months').format('YYYY-MM-DD'),
        })
      } else {
        this.applyFilter({ minDateExpire: null, maxDateExpire: null })
      }
    },
    openMobileEdit(item) {
      if (!this.editForms[item.id]) {
        this.$set(this.editForms, item.id, {
          available_date: item.availble_date || null,
          expire_date:    item.expire_date || null,
          /*
           * ⚠️ `|| ''` กลืนเลข 0 ทิ้ง — 0 คือ "On request" (ลูกค้าไม่บอกราคา) ถ้ากลายเป็นค่าว่าง
           * ช่องกรอกจะโล่ง แล้วกดบันทึกทีเดียวจะถอดประกาศฝั่งนั้นทิ้งโดยไม่ตั้งใจ
           */
          sale_price:     this.hasOffer(item.sale_price) ? item.sale_price : '',
          rent_price:     this.hasOffer(item.rent_price) ? item.rent_price : '',
          statusObj:      this.statuses.find(s => s.id === item.status_id) || null,
          isUpdating:     false,
        })
      }
      this.mobileEditItem = item
    },
    toggleRowDetails(item) {
      const wasOpen = item._showDetails
      this.properties.forEach(p => { if (p._showDetails) this.$set(p, '_showDetails', false) })
      if (!wasOpen) {
        this.$set(item, '_showDetails', true)
        if (!this.editForms[item.id]) {
          this.$set(this.editForms, item.id, {
            available_date: item.availble_date || null,
            expire_date:    item.expire_date || null,
            /*
             * ⚠️ `|| ''` กลืนเลข 0 ทิ้ง — 0 คือ "On request" (ลูกค้าไม่บอกราคา) ถ้ากลายเป็นค่าว่าง
             * ช่องกรอกจะโล่ง แล้วกดบันทึกทีเดียวจะถอดประกาศฝั่งนั้นทิ้งโดยไม่ตั้งใจ
             */
            sale_price:     this.hasOffer(item.sale_price) ? item.sale_price : '',
            rent_price:     this.hasOffer(item.rent_price) ? item.rent_price : '',
            statusObj:      this.statuses.find(s => s.id === item.status_id) || null,
            isUpdating:     false,
          })
        }
      }
    },
    async updatePropertyQuick(item) {
      const form = this.editForms[item.id]
      form.isUpdating = true
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        await axios.post('property/v1/update/list', {
          property_id:    item.id,
          available_date: form.available_date,
          expire_date:    form.expire_date,
          salePrice:      form.sale_price || null,
          rentPrice:      form.rent_price || null,
          status:         form.statusObj || null,
          updated_by:     userData.id,
        })
        this.$set(item, 'availble_date', form.available_date)
        this.$set(item, 'expire_date',   form.expire_date)
        this.$set(item, 'sale_price',    form.sale_price ? Number(form.sale_price) : null)
        this.$set(item, 'rent_price',    form.rent_price ? Number(form.rent_price) : null)
        if (form.statusObj) {
          this.$set(item, 'status_id',   form.statusObj.id)
          this.$set(item, 'status_code', form.statusObj.name)
        }
        this.$bvToast.toast('Property updated', { title: 'Success', variant: 'success', solid: true, toaster: 'b-toaster-top-right' })
      } catch {
        this.$bvToast.toast('Update failed', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' })
      } finally {
        form.isUpdating = false
      }
    },
    /*
     * ราคามีสามสถานะ: > 0 = ราคาจริง, 0 = "On request" (ลูกค้าไม่บอกราคาแต่ลงประกาศไว้),
     * ค่าว่าง = ไม่ได้ลงประกาศฝั่งนั้น — ฝั่ง API ใช้กติกาเดียวกัน (ดูตาราง property_offer)
     */
    /** ลงประกาศฝั่งนี้ไหม — 0 (สอบถามราคา) ก็นับ ต่างจากค่าว่างที่แปลว่าไม่ได้ลงประกาศ */
    hasOffer(val) {
      if (val === null || val === undefined || val === '') return false
      return !isNaN(Number(val))
    },
    isOnRequest(val) {
      return this.hasOffer(val) && Number(val) === 0
    },
    /** ข้อความราคาในตาราง — ใส่ ฿ ให้เฉพาะตอนที่มีตัวเลขจริง */
    priceCell(val) {
      return this.isOnRequest(val) ? 'On request' : `฿ ${this.formatPriceDisplay(val)}`
    },
    formatPriceDisplay(val) {
      if (!val && val !== 0) return ''
      const num = Math.round(parseFloat(String(val).replace(/,/g, '')))
      if (isNaN(num)) return ''
      return num === 0 ? 'On request' : num.toLocaleString('en-US')
    },
    /*
     * ⚠️ ช่องกรอกใช้ตัวนี้ ไม่ใช่ formatPriceDisplay — ถ้าใส่คำว่า "On request" ลงใน input
     * ผู้ใช้จะแก้ต่อไม่ได้ และ onPriceInput จะถอดตัวอักษรทิ้งจนกลายเป็นค่าว่าง
     * (= ถอดประกาศฝั่งนั้นทิ้ง) 0 จึงต้องโชว์เป็นเลข 0 ตามจริง
     */
    formatPriceInput(val) {
      if (!val && val !== 0) return ''
      const num = Math.round(parseFloat(String(val).replace(/,/g, '')))
      if (isNaN(num)) return ''
      return num.toLocaleString('en-US')
    },
    onPriceInput(itemId, field, e) {
      const raw = e.target.value.replace(/[^0-9]/g, '')
      const formatted = raw ? Number(raw).toLocaleString('en-US') : ''
      e.target.value = formatted
      this.$set(this.editForms[itemId], field, raw)
    },
    startSidebarResize(e) {
      e.preventDefault()
      const onMove = ev => {
        const w = window.innerWidth - ev.clientX
        this.sidebarWidth = Math.min(800, Math.max(300, w))
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    openComment(id) {
      this.commentPropertyId = id
      this.isSidebarCommentOpen = true
    },
    buildMailto(item) {
      const placeName = item.place_name?.en || ''
      const subject = encodeURIComponent(`${placeName} (${item.property_code})`)
      const bedroom = item.bedroom === 0 ? 'Studio' : `${item.bedroom} bedroom`
      let body = `${placeName} (${item.property_code})\n`
      body += `${bedroom} ${item.sqm} sq.m.`
      if (item.floors) body += ` ${item.floors} Fl.`
      body += '\n'
      if (item.furniture_name) body += `${item.furniture_name}\n`
      if (item.sale_price > 0) body += `Sale Price : ${this.formatPriceDisplay(item.sale_price)} THB\n`
      if (item.rent_price > 0) body += `Rent Price : ${this.formatPriceDisplay(item.rent_price)} THB\n`
      body += this.frontendUrl(item)
      return `mailto:?subject=${subject}&body=${encodeURIComponent(body)}`
    },
    async socialPost(item) {
      if (this.postingIds.has(item.id)) return
      this.postingIds = new Set([...this.postingIds, item.id])
      this.$bvToast.toast('Posting to Facebook...', { title: 'Facebook', variant: 'info', solid: true, toaster: 'b-toaster-top-right' })
      try {
        const res = await axios.get(`/v1/automate-post/page/${item.id}`)
        if (res.data.success) {
          this.$bvToast.toast('Posted to Facebook successfully', { title: 'Facebook', variant: 'success', solid: true, toaster: 'b-toaster-top-right' })
        } else {
          this.$bvToast.toast(res.data.message || 'Failed to post', { title: 'Facebook', variant: 'warning', solid: true, toaster: 'b-toaster-top-right' })
        }
      } catch {
        this.$bvToast.toast('Could not post to Facebook', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' })
      } finally {
        const next = new Set(this.postingIds)
        next.delete(item.id)
        this.postingIds = next
      }
    },
    async addToCart(item) {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        await axios.post('/property/v1/add-cart/', { property_id: item.id, user_id: userData.id })
        const current = this.$store.state['app-p'].cartItemsCount || 0
        this.$store.commit('app-p/UPDATE_CART_ITEMS_COUNT', current + 1)
        this.$bvToast.toast('Added to cart', { title: 'Cart', variant: 'info', solid: true, toaster: 'b-toaster-top-right' })
      } catch {
        this.$bvToast.toast('Could not add to cart', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' })
      }
    },
    slugify(text) {
      return (text || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    frontendUrl(item) {
      const slug = this.slugify(item.place_name?.en)
      return `https://therealtors.net/condo/${slug}-${item.id}`
    },
    shareWithText(item) {
      const placeName = item.place_name?.en || ''
      let text = `${placeName} (${item.property_code})\n`
      const bedroom = item.bedroom === 0 ? 'Studio' : `${item.bedroom} bedroom`
      text += `${bedroom} ${item.sqm} sq.m.`
      if (item.floors) text += ` ${item.floors} Fl.`
      text += '\n'
      if (item.furniture_name) text += `${item.furniture_name}\n`
      if (item.sale_price > 0) text += `Sale Price : ${this.formatPriceDisplay(item.sale_price)} THB\n`
      if (item.rent_price > 0) text += `Rent Price : ${this.formatPriceDisplay(item.rent_price)} THB\n`
      text += this.frontendUrl(item)
      this.$copyText(text)
        .then(() => this.$bvToast.toast('Text & link copied', { title: 'Copied', variant: 'success', solid: true, toaster: 'b-toaster-top-right' }))
        .catch(() => this.$bvToast.toast('Could not copy', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' }))
    },
    copyLink(item) {
      this.$copyText(this.frontendUrl(item))
        .then(() => this.$bvToast.toast('Link copied', { title: 'Copied', variant: 'success', solid: true, toaster: 'b-toaster-top-right' }))
        .catch(() => this.$bvToast.toast('Could not copy link', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' }))
    },
    copyLinkNetwork(item) {
      const slug = this.slugify(item.place_name?.en)
      this.$copyText(`https://thepropology.com/condo/${slug}-${item.id}`)
        .then(() => this.$bvToast.toast('Network link copied', { title: 'Copied', variant: 'success', solid: true, toaster: 'b-toaster-top-right' }))
        .catch(() => this.$bvToast.toast('Could not copy link', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' }))
    },
    onClearFilter() {
      this.toolbarStatusObj = null
      this.toolbarExpiringSoon = false
      this.clearFilter()
    },
  },
}
</script>

<style scoped>
.prop-thumb { width: 160px; overflow: hidden; }

/* Share bottom sheet */
.share-sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
}
.share-sheet {
  width: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 0.75rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.share-sheet-handle {
  width: 40px; height: 4px;
  background: #d0d0d0;
  border-radius: 2px;
  margin-bottom: 0.75rem;
}
.share-sheet-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #4b465c;
  margin-bottom: 1rem;
  width: 100%;
  text-align: center;
}
.share-sheet-actions {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  margin-bottom: 1rem;
}
.share-sheet-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #4b465c;
  font-size: 0.72rem;
  padding: 0;
  text-decoration: none;
}
.share-sheet-icon {
  width: 48px; height: 48px;
  border-radius: 50%;
  background: #f4f4f4;
  display: flex;
  align-items: center;
  justify-content: center;
}
.share-sheet-cancel {
  width: 100%;
  padding: 0.65rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: none;
  font-size: 0.9rem;
  color: #6e6b7b;
  cursor: pointer;
}
/* Slide-up transition */
.slide-up-enter-active, .slide-up-leave-active { transition: opacity 0.25s, transform 0.25s; }
.slide-up-enter, .slide-up-leave-to { opacity: 0; }
.slide-up-enter .share-sheet, .slide-up-leave-to .share-sheet { transform: translateY(100%); }
.share-sheet { transition: transform 0.25s; }

.prop-card { transition: background 0.15s; }
.prop-card:hover { background: #fafafa; }


.min-width-0 { min-width: 0; }

.mobile-action-btn {
  padding: 0.4rem !important;
  line-height: 1;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.row-detail-panel { background: transparent; border-top: 2px solid #ebe9f1; }
.detail-section-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #b9b9c3; margin-bottom: 0.4rem; }
.detail-key { display: block; font-size: 0.72rem; color: #b9b9c3; margin-bottom: 0.1rem; }
.detail-label { display: block; font-size: 0.72rem; font-weight: 600; color: #5e5873; margin-bottom: 0.25rem; }
.cursor-pointer { cursor: pointer; }
.sidebar-resize-handle {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 5px;
  cursor: col-resize;
  z-index: 10;
  background: transparent;
  transition: background 0.15s;
}
.sidebar-resize-handle:hover { background: rgba(115, 103, 240, 0.2); }
.edit-field-col { flex: 1 1 160px; min-width: 160px; }
.detail-close-btn { position: absolute; top: 0.25rem; right: 0.25rem; padding: 0.25rem; line-height: 1; }
.edit-field-col ::v-deep .vs__dropdown-toggle {
  height: 38px;
  min-height: 38px;
  padding-top: 0;
  padding-bottom: 0;
  background-color: #fff;
}
.edit-field-col ::v-deep .vs__selected,
.edit-field-col ::v-deep .vs__search {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 1.5;
}

/* Pagination prev/next icons */
.prev-item .page-link, .next-item .page-link {
  display: flex; align-items: center; justify-content: center;
}

/* Desktop table: always-visible horizontal scrollbar + edge fade so users
   on macOS (where scrollbars auto-hide) can see there's more to scroll to. */
.property-list-v2 ::v-deep .table-responsive {
  background:
    linear-gradient(to right, #fff 30%, rgba(255, 255, 255, 0)),
    linear-gradient(to right, rgba(255, 255, 255, 0), #fff 70%) 100% 0,
    linear-gradient(to right, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) 0 0,
    linear-gradient(to left, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) 100% 0;
  background-repeat: no-repeat;
  background-size: 40px 100%, 40px 100%, 14px 100%, 14px 100%;
  background-attachment: local, local, scroll, scroll;
  scrollbar-width: thin;
  scrollbar-color: #b9b9c3 #f4f4f4;
}
.property-list-v2 ::v-deep .table-responsive::-webkit-scrollbar {
  height: 8px;
}
.property-list-v2 ::v-deep .table-responsive::-webkit-scrollbar-track {
  background: #f4f4f4;
  border-radius: 4px;
}
.property-list-v2 ::v-deep .table-responsive::-webkit-scrollbar-thumb {
  background: #b9b9c3;
  border-radius: 4px;
}
.property-list-v2 ::v-deep .table-responsive::-webkit-scrollbar-thumb:hover {
  background: #7367f0;
}
</style>
